/**
 * Read-only port of student-dashboard-v2/src/app/(dashboard)/useCardComponentLogic.ts.
 *
 * Keeps the student's listing (processedQuestions), card-click handling, and
 * question-stepping (moveToNextQuestion) — including per-question media loading
 * via loadMediaUrl and type-based modal routing — but strips everything tied to
 * answering: no recording, no answer submission, no redux, no freemium/enrollment.
 */
import {useCallback, useMemo, useState} from 'react';
import {PlacementTestsService} from '../PlacementTest/PlacementTestsService';
import {shouldUseMCQModal, shouldUseTypedAnswerModal} from './helpers';
import {
  isPlacementTest,
  isVideoMedia,
  type QuestionCardData,
  type SimpleQuestion,
  type SimpleTest,
  type UIQuestion,
} from './simpleTypes';

const placementTestsService = new PlacementTestsService();

type LoadMediaUrl = (url: string) => Promise<string | null>;

export const useExercisePreview = ({
  tests,
  loadMediaUrl,
}: {
  tests: SimpleTest[];
  loadMediaUrl: LoadMediaUrl;
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<UIQuestion | null>(
    null,
  );
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isMCQModalOpen, setIsMCQModalOpen] = useState(false);
  const [isTypedAnswerModalOpen, setIsTypedAnswerModalOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<
    {isVideo: boolean; videoUrl?: string; thumbnailUrl?: string} | undefined
  >(undefined);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [loadedTests, setLoadedTests] = useState<Map<string, SimpleTest>>(
    new Map(),
  );

  // Convert SimpleTest[] to display cards (mirrors the student's processedQuestions).
  const processedQuestions = useMemo(() => {
    return tests
      .filter(test => {
        const isPlacement = isPlacementTest(test);
        return isPlacement || (test.questions && test.questions.length > 0);
      })
      .sort((a, b) => {
        const aIsPlacement = isPlacementTest(a);
        const bIsPlacement = isPlacementTest(b);
        if (aIsPlacement && !bIsPlacement) return -1;
        if (!aIsPlacement && bIsPlacement) return 1;
        return 0;
      })
      .map(test => {
        const isPlacement = isPlacementTest(test);
        const hasQuestions = test.questions && test.questions.length > 0;
        const firstQuestion = hasQuestions ? test.questions?.[0] : null;
        const compositeId = `${test.type}-${test._id}`;
        const hasTestVideo = isVideoMedia(test.media);
        const testVideoUrl = hasTestVideo ? test.media?.[0].url : undefined;
        const testThumbnailUrl = test.thumbnail;

        const cardData: QuestionCardData = {
          id: compositeId,
          testId: test._id,
          testType: test.type,
          name: test.name || 'Untitled',
          title: test.name || 'Untitled',
          summary: test.description,
          description: test.description,
          url: test.thumbnail,
          image: test.thumbnail,
          thumbnailUrl: test.thumbnail,
          isVideo: hasTestVideo || false,
          isBlur: false,
          showRedButton:
            isPlacement ||
            (test.questions && test.questions.length > 1) ||
            false,
          answered: false,
          totalQuestionsInTest: test.questions?.length || 0,
          currentQuestionIndex: 0,
          testLevelMedia: hasTestVideo
            ? {
                isVideo: true,
                videoUrl: testVideoUrl,
                thumbnailUrl: testThumbnailUrl,
              }
            : undefined,
          ...(firstQuestion && {
            text: firstQuestion.questionText,
            textAI: firstQuestion.AiText,
            payload: firstQuestion.payload,
            originalQuestionId: firstQuestion._id,
          }),
        };

        return cardData;
      });
  }, [tests]);

  const closeModals = useCallback(() => {
    setIsQuestionModalOpen(false);
    setIsMCQModalOpen(false);
    setIsTypedAnswerModalOpen(false);
    setIsPreviewMode(false);
    setPreviewMedia(undefined);
    setSelectedQuestion(null);
  }, []);

  // Present a test's first question with the correct modal routing.
  const presentFirstQuestion = useCallback(
    async (test: SimpleTest, questions: SimpleQuestion[]) => {
      const firstQuestion = questions[0];
      let mediaUrl: string | undefined;
      let testMediaUrl: string | undefined;
      let isVideo = false;

      if (
        firstQuestion.payload?.media &&
        firstQuestion.payload.media.length > 0
      ) {
        const loadedUrl = await loadMediaUrl(
          firstQuestion.payload.media[0].url,
        );
        if (loadedUrl) {
          mediaUrl = loadedUrl;
          isVideo = isVideoMedia(firstQuestion.payload.media);
        }
      }

      if (test.media && test.media.length > 0) {
        const loadedTestUrl = await loadMediaUrl(test.media[0].url);
        if (loadedTestUrl) testMediaUrl = loadedTestUrl;
      }

      const uiQuestion: UIQuestion = {
        ...firstQuestion,
        testId: test._id,
        testType: test.type,
        testName: test.name,
        testThumbnail: test.thumbnail,
        studentPlanLevelId: test.studentPlanLevelId,
        studentPlanCurriculumId: test.studentPlanCurriculumId,
        mediaUrl,
        url: mediaUrl,
        image: mediaUrl,
        isVideo,
        currentQuestionIndex: 0,
        totalQuestionsInTest: questions.length,
        isAnswered: false,
        hasOwnMedia: !!(
          firstQuestion.payload?.media && firstQuestion.payload.media.length > 0
        ),
      };

      setSelectedQuestion(uiQuestion);

      const isMCQ = shouldUseMCQModal(firstQuestion);
      const hasIntroVideo = isVideoMedia(test.media);

      setPreviewMedia(
        hasIntroVideo
          ? {
              isVideo: true,
              videoUrl: testMediaUrl || test.media?.[0].url,
              thumbnailUrl: test.thumbnail,
            }
          : undefined,
      );

      if (hasIntroVideo) {
        setIsPreviewMode(true);
        setIsQuestionModalOpen(true);
      } else if (isMCQ) {
        setIsPreviewMode(false);
        setIsMCQModalOpen(true);
      } else if (shouldUseTypedAnswerModal(firstQuestion)) {
        setIsTypedAnswerModalOpen(true);
      } else {
        setIsPreviewMode(false);
        setIsQuestionModalOpen(true);
      }
    },
    [loadMediaUrl],
  );

  const handleCardClick = useCallback(
    async (compositeId: string) => {
      const test = tests.find(t => `${t.type}-${t._id}` === compositeId);
      if (!test) return;

      if (isPlacementTest(test)) {
        setIsCreatingSession(true);
        try {
          const fullTest = await placementTestsService.getPlacementTestById(
            test._id,
          );
          const questions = (fullTest?.questions ?? []) as SimpleQuestion[];
          if (!questions.length) return;

          const testWithQuestions: SimpleTest = {
            ...test,
            questions,
            media: fullTest?.media ?? test.media,
            thumbnail: fullTest?.thumbnail ?? test.thumbnail,
          };
          setLoadedTests(prev =>
            new Map(prev).set(test._id, testWithQuestions),
          );
          await presentFirstQuestion(testWithQuestions, questions);
        } catch (error) {
          console.error('[PlanPreview] Failed to load placement test:', error);
        } finally {
          setIsCreatingSession(false);
        }
      } else {
        if (!test.questions || test.questions.length === 0) return;
        await presentFirstQuestion(test, test.questions);
      }
    },
    [tests, presentFirstQuestion],
  );

  // Advance to the next question in the active test (no answering).
  const moveToNextQuestion = useCallback(async () => {
    if (!selectedQuestion) return;

    const currentIndex = selectedQuestion.currentQuestionIndex || 0;
    const totalQuestions = selectedQuestion.totalQuestionsInTest || 1;

    if (currentIndex >= totalQuestions - 1) {
      closeModals();
      return;
    }

    const testId = selectedQuestion.testId;
    const testType = selectedQuestion.testType;

    let test = tests.find(t => t._id === testId);
    if (testType === 'PLACEMENT_TEST' && testId) {
      const loaded = loadedTests.get(testId);
      if (loaded) test = loaded;
    }

    if (!test || !test.questions || test.questions.length === 0) return;

    const nextIndex = currentIndex + 1;
    const nextQuestion = test.questions[nextIndex];
    if (!nextQuestion) return;

    let mediaUrl: string | undefined;
    let isVideo = false;
    if (nextQuestion.payload?.media && nextQuestion.payload.media.length > 0) {
      const loadedUrl = await loadMediaUrl(nextQuestion.payload.media[0].url);
      if (loadedUrl) {
        mediaUrl = loadedUrl;
        isVideo = isVideoMedia(nextQuestion.payload.media);
      }
    }

    const uiQuestion: UIQuestion = {
      ...nextQuestion,
      testId: test._id,
      testType: test.type,
      testName: test.name,
      testThumbnail: test.thumbnail,
      studentPlanLevelId: selectedQuestion.studentPlanLevelId,
      studentPlanCurriculumId: selectedQuestion.studentPlanCurriculumId,
      mediaUrl,
      url: mediaUrl,
      image: mediaUrl,
      isVideo,
      currentQuestionIndex: nextIndex,
      totalQuestionsInTest: test.questions.length,
      isAnswered: false,
      hasOwnMedia: !!(
        nextQuestion.payload?.media && nextQuestion.payload.media.length > 0
      ),
    };

    setSelectedQuestion(uiQuestion);
    setPreviewMedia(undefined);
    setIsPreviewMode(false);

    const isMCQ = shouldUseMCQModal(nextQuestion);
    if (isMCQ) {
      setIsQuestionModalOpen(false);
      setIsTypedAnswerModalOpen(false);
      setIsMCQModalOpen(true);
    } else if (shouldUseTypedAnswerModal(nextQuestion)) {
      setIsQuestionModalOpen(false);
      setIsMCQModalOpen(false);
      setIsTypedAnswerModalOpen(true);
    } else {
      setIsMCQModalOpen(false);
      setIsTypedAnswerModalOpen(false);
      setIsQuestionModalOpen(true);
    }
  }, [selectedQuestion, tests, loadedTests, loadMediaUrl, closeModals]);

  // From the intro-video preview, advance to the first actual question modal.
  const goToQuestionFromPreview = useCallback(() => {
    setIsPreviewMode(false);
    setPreviewMedia(undefined);
    if (!selectedQuestion) return;

    if (shouldUseMCQModal(selectedQuestion)) {
      setIsQuestionModalOpen(false);
      setIsMCQModalOpen(true);
    } else if (shouldUseTypedAnswerModal(selectedQuestion)) {
      setIsQuestionModalOpen(false);
      setIsTypedAnswerModalOpen(true);
    } else {
      // Recording-type question: keep the QuestionModal open as the question screen.
      setIsQuestionModalOpen(true);
    }
  }, [selectedQuestion]);

  return {
    processedQuestions,
    selectedQuestion,
    isQuestionModalOpen,
    isMCQModalOpen,
    isTypedAnswerModalOpen,
    isPreviewMode,
    previewMedia,
    isCreatingSession,
    handleCardClick,
    moveToNextQuestion,
    goToQuestionFromPreview,
    closeModals,
  };
};
